<template>
  <!--
    The card is the control: the textarea drops its own border, fill and
    focus ring so the whole composer lights up as one field. `focus-within`
    rather than `focus-visible` because the ring belongs to the wrapper,
    which is not itself focusable.
  -->
  <div
    class="p-3.5 pb-3 bg-card border border-input rounded-ds-md shadow-ds-sm
      transition-[border-color,box-shadow] duration-ds-fast ease-ds-standard
      focus-within:border-ring focus-within:shadow-ds-focus"
  >
    <DSTextarea
      ref="composerRef"
      v-model="sentence"
      :rows="2"
      class="min-h-22 p-0 resize-none border-0 bg-transparent
        focus-visible:border-0 focus-visible:shadow-none rounded-none"
      placeholder="I bought a house with a mortgage"
      aria-label="Describe what happened"
      @keydown.enter.meta.prevent="submit"
      @keydown.enter.ctrl.prevent="submit"
    />
    <div class="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
      <div
        v-tooltip.permanent="'Not available yet. Describe it in your own words instead.'"
        class="flex flex-wrap gap-2"
      >
        <!--
          A statement is just text to the assistant, so this focuses the
          field and lets the user paste — no separate parsing surface.
        -->
        <DSButton
          variant="secondary"
          size="sm"
          disabled
          class="border-border font-medium"
          @click="focusComposer"
        >
          <ClipboardPaste class="size-3.5" />
          Paste a statement
        </DSButton>
        <!--
          Disabled deliberately: the assistant is text-only today
          (AIConversationForm accepts no attachment), so there is nowhere
          for a file to go. Kept visible because the spec puts it here,
          and labelled rather than silently inert.
        -->
        <DSButton
          variant="secondary"
          size="sm"
          disabled
          class="border-border font-medium"
        >
          <ImageUp class="size-3.5" />
          Upload a document or photo
        </DSButton>
      </div>
      <DSButton
        size="sm"
        :disabled="!isSubmittable"
        @click="submit"
      >
        Continue
      </DSButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { ClipboardPaste, ImageUp } from '@lucide/vue'

import { DSButton, DSTextarea } from '@ds'

const emit = defineEmits<{
  (e: 'describe', sentence: string): void
}>()

const composerRef = useTemplateRef<{ $el: HTMLTextAreaElement }>('composerRef')

const sentence = ref('')

const isSubmittable = computed(() => !!sentence.value.trim())

const focusComposer = () => {
  composerRef.value?.$el?.focus()
}

const submit = () => {
  if (!isSubmittable.value) return

  emit('describe', sentence.value.trim())
}
</script>
